import { IDomainProduct } from "@src/models/DomainProduct";
import { IProductPrice } from "@src/models/ProductPrice";
import { computed, onBeforeUnmount, ref, watch } from "vue";
import { useApi, useApiUrl, useErrorHandler } from "@src/composables/api";
import { useI18n } from "@src/composables/i18n";
import { useMoney } from "@src/composables/utils";
import { useUrl } from "@src/composables/url";

import _map from "lodash/map";
import _orderBy from "lodash/orderBy";
import _uniqBy from "lodash/uniqBy";

export interface IDomainProductMapped {
  billing_cycle_years: IProductPrice["billing_cycle_months"];
  billing_summary: string;
  domain: string;
  is_available: boolean;
  is_discounted: boolean;
  percentage_saving: number;
  pid: IDomainProduct["id"];
  price_discounted_formatted: IProductPrice["price_discounted_formatted"];
  price_formatted: IProductPrice["price_formatted"];
  sld: string;
  tld: IDomainProduct["tld"];
}

const URL_QUERY_PARAM = "upm-dac-query";

export function useDac({
  coupons = [],
  currencyCode,
  limit = 10,
  orderConfigUrl = "",
  tlds = [],
}: {
  coupons?: string[];
  currencyCode?: string;
  limit: number;
  orderConfigUrl: string;
  tlds?: string[];
}) {
  // Composables

  const { $tc } = useI18n({ packs: ["dac"] });
  const { useApiGet } = useApi();
  const { removeTrailingZeroes } = useMoney();
  const { getParamFromUrl, syncParamToUrl } = useUrl();

  // Data

  const controller = ref(null as null | AbortController);
  const domain = ref(getParamFromUrl(URL_QUERY_PARAM) || "");
  const hasError = ref(false);
  const isProcessing = ref(false);
  const results = ref([] as any[]);
  const resultsTotal = ref(0);
  const timeout = ref(null as null | NodeJS.Timeout);

  // Life-Cycle Hooks

  onBeforeUnmount(() => {
    syncParamToUrl(URL_QUERY_PARAM);
  });

  // Computed

  const resultsCount = computed(() => {
    return results.value.length;
  });

  const hasMoreResults = computed(() => {
    return !!resultsCount.value && resultsCount.value < resultsTotal.value;
  });

  const hasNoResults = computed(() => {
    return !resultsCount.value && !!resultsTotal.value;
  });

  const sanitisedDomain = computed(() => {
    return domain.value
      .replace(/(^https?:\/\/)?(w{3}\.)?[^a-z0-9\-\.]?/gi, "")
      .toLowerCase();
  });

  const sanitisedSld = computed(() => {
    return sanitisedDomain.value.split(".")?.[0];
  });

  const sanitisedTld = computed(() => {
    const matches = sanitisedDomain.value.match(/(?:^[^\.]+)(\..{2,})/i);
    return matches?.[1] || "";
  });

  // Methods

  function resetSearch() {
    if (controller.value) controller.value.abort();
    if (timeout.value) clearTimeout(timeout.value);
    hasError.value = false;
  }

  function doSearch(delay: number = 0): void {
    resetSearch();
    timeout.value = setTimeout(() => {
      resetResults();
      syncParamToUrl(URL_QUERY_PARAM, sanitisedDomain.value);
      search(sanitisedSld.value, sanitisedTld.value);
    }, delay);
  }

  function debounceSearch(query: string) {
    if (!query)
      return Promise.all([
        resetSearch(),
        resetResults(),
        syncParamToUrl(URL_QUERY_PARAM),
      ]);
    doSearch(300);
  }

  function resetResults(): void {
    results.value = [];
    resultsTotal.value = 0;
  }

  function clearSearch(): void {
    domain.value = "";
  }

  function loadMore() {
    return search(sanitisedSld.value, sanitisedTld.value, resultsCount.value);
  }

  async function search(sld: string, tld: string, offset?: number) {
    try {
      if (!sld.length) return;
      isProcessing.value = true;
      controller.value = new AbortController();

      // Construct the search URL
      const relations = ["prices", "options", "options.prices", "attributes"];
      const url = useApiUrl("modules/web_hosting/domains/search");
      url.searchParams.set("sld", sld);
      url.searchParams.set("with", relations.join(","));
      url.searchParams.set("limit", limit.toString());
      if (offset) url.searchParams.set("offset", offset.toString());
      if (currencyCode) url.searchParams.set("currency_code", currencyCode);
      if (tld) url.searchParams.set("tld", tld);
      if (Array.isArray(tlds)) {
        for (const tld of tlds) url.searchParams.append("tlds[]", tld);
      }
      if (coupons.length) url.searchParams.set("promotions", coupons.join());

      // Fetch the search results
      const { data: response } = await useApiGet(
        { url, init: { signal: controller.value.signal } },
        false
      );

      // Update response total
      resultsTotal.value = response.total;

      // Push new data to results array
      results.value.push(...mapResults(sld, response.data));

      // Remove potential of duplicates
      results.value = _uniqBy(results.value, "domain");
    } catch (e) {
      useErrorHandler(e, () => (hasError.value = true));
    } finally {
      isProcessing.value = false;
    }
  }

  function mapResults(
    sld: string,
    domains = [] as IDomainProduct[]
  ): IDomainProductMapped[] {
    return _map(domains, (domain) => {
      const term = _orderBy(domain.prices, "billing_cycle_months", "asc")?.[0];
      const billing_cycle_years = Math.round(term.billing_cycle_months / 12);
      const can_transfer =
        domain?.meta?.overrides?.dac?.canTransfer ??
        domain?.category?.meta?.overrides?.dac?.canTransfer ??
        true;
      const is_discounted = !!term.price_discounted_formatted;
      const price = term.price;
      const price_discounted = term.price_discounted ?? 0;
      const price_formatted = removeTrailingZeroes(term.price_formatted);
      const price_discounted_formatted = removeTrailingZeroes(
        term.price_discounted_formatted
      );

      return {
        billing_cycle_years,
        billing_summary: $tc(
          is_discounted
            ? "dac.billing_summary_discounted"
            : "dac.billing_summary",
          billing_cycle_years,
          {
            oldPrice: price_formatted,
            newPrice: price_discounted_formatted,
          }
        ),
        can_transfer,
        domain: [sld, domain.tld].join(""),
        is_available: domain.domain_available,
        is_numerical: !isNaN(sld as any),
        is_discounted,
        order_url: constructOrderUrl(sld, domain, term),
        percentage_saving: is_discounted
          ? Math.floor(((price - price_discounted) / price) * 100)
          : 0,
        pid: domain.id,
        price_discounted_formatted,
        price_formatted,
        sld,
        tld: domain.tld,
      };
    });
  }

  function constructOrderUrl(
    sld: string,
    domain: IDomainProduct,
    term: IProductPrice
  ) {
    const url = new URL(orderConfigUrl);
    url.searchParams.set("pid", domain.id);
    url.searchParams.set("bcm", term.billing_cycle_months.toString());
    url.searchParams.set("currency", term.currency_code);
    url.searchParams.set("pfields[sld]", sld);
    if (domain.sub_product_id)
      url.searchParams.set("sub_pids", domain.sub_product_id);
    // Add `coupons` parameter for applied promotions
    const codes = _map(term?.promotions, "code");
    if (codes.length) url.searchParams.set("coupons", codes.join());

    return url;
  }

  watch(sanitisedDomain, debounceSearch, { immediate: true });

  return {
    domain,
    hasError,
    hasMoreResults,
    hasNoResults,
    isProcessing,
    results,
    resultsCount,
    resultsTotal,
    sanitisedDomain,
    sanitisedSld,
    sanitisedTld,
    clearSearch,
    doSearch,
    loadMore,
  };
}
